
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { formatCertificateNumber } from '@/utils/certificateUtils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertCircle,
  Check,
  Download,
  Filter,
  Loader2,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { createCertificateAuditRecord } from '@/utils/auditUtils';

interface CertificateExportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ExportFilter {
  state?: string;
  courseType?: string;
  startDate?: string;
  endDate?: string;
  isUsed?: boolean;
  isVerified?: boolean;
  isReported?: boolean;
}

export function CertificateExportModal({
  open,
  onOpenChange,
}: CertificateExportModalProps) {
  const { toast } = useToast();
  const [filters, setFilters] = useState<ExportFilter>({
    state: undefined,
    courseType: undefined,
    startDate: format(new Date(new Date().getFullYear(), 0, 1), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
    isUsed: undefined,
    isVerified: undefined,
    isReported: undefined,
  });
  const [exportFormat, setExportFormat] = useState<'csv' | 'xlsx' | 'regulatory'>('csv');
  const [isExporting, setIsExporting] = useState(false);
  
  // Get available states and course types
  const { data: metadata } = useQuery({
    queryKey: ['certificateExportMetadata'],
    queryFn: async () => {
      // Get distinct states
      const { data: stateData } = await supabase
        .from('certificate_number_inventory')
        .select('state')
        .limit(1000);
      
      // Get distinct course types
      const { data: courseTypeData } = await supabase
        .from('certificate_number_inventory')
        .select('course_type')
        .limit(1000);
        
      // Extract unique values
      const states = Array.from(new Set(stateData?.map(item => item.state) || []));
      const courseTypes = Array.from(new Set(courseTypeData?.map(item => item.course_type) || []));
      
      return { states, courseTypes };
    },
    enabled: open,
  });
  
  // Generate preview count
  const { data: previewCount, isLoading: isCountLoading } = useQuery({
    queryKey: ['certificateExportPreview', filters],
    queryFn: async () => {
      let query = supabase
        .from('certificate_number_inventory')
        .select('*', { count: 'exact', head: true });
      
      // Apply filters
      if (filters.state) {
        query = query.eq('state', filters.state);
      }
      
      if (filters.courseType) {
        query = query.eq('course_type', filters.courseType);
      }
      
      if (filters.startDate) {
        query = query.gte('created_at', filters.startDate);
      }
      
      if (filters.endDate) {
        const endDateWithTime = `${filters.endDate}T23:59:59`;
        query = query.lte('created_at', endDateWithTime);
      }
      
      if (filters.isUsed !== undefined) {
        query = query.eq('is_used', filters.isUsed);
      }
      
      if (filters.isVerified !== undefined) {
        query = filters.isVerified
          ? query.eq('verification_status', 'verified')
          : query.neq('verification_status', 'verified');
      }
      
      if (filters.isReported !== undefined) {
        query = filters.isReported
          ? query.eq('reporting_status', 'reported')
          : query.neq('reporting_status', 'reported');
      }
      
      const { count, error } = await query;
      
      if (error) throw error;
      return count || 0;
    },
    enabled: open,
  });
  
  const handleExport = async () => {
    setIsExporting(true);
    
    try {
      // Construct the query
      let query = supabase
        .from('certificate_number_inventory')
        .select(`
          *,
          certificates:used_by (
            id,
            user_id,
            issued_at,
            unique_code,
            is_revoked,
            profiles:user_id (
              full_name,
              email,
              phone_number,
              dob,
              driver_license_number,
              driver_license_state
            )
          )
        `);
      
      // Apply filters
      if (filters.state) {
        query = query.eq('state', filters.state);
      }
      
      if (filters.courseType) {
        query = query.eq('course_type', filters.courseType);
      }
      
      if (filters.startDate) {
        query = query.gte('created_at', filters.startDate);
      }
      
      if (filters.endDate) {
        const endDateWithTime = `${filters.endDate}T23:59:59`;
        query = query.lte('created_at', endDateWithTime);
      }
      
      if (filters.isUsed !== undefined) {
        query = query.eq('is_used', filters.isUsed);
      }
      
      if (filters.isVerified !== undefined) {
        query = filters.isVerified
          ? query.eq('verification_status', 'verified')
          : query.neq('verification_status', 'verified');
      }
      
      if (filters.isReported !== undefined) {
        query = filters.isReported
          ? query.eq('reporting_status', 'reported')
          : query.neq('reporting_status', 'reported');
      }
      
      // Execute the query
      const { data, error } = await query.order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Format the data for export based on the selected format
      let exportData;
      let filename;
      let mimeType;
      
      const dateStr = format(new Date(), 'yyyy-MM-dd');
      
      if (exportFormat === 'regulatory') {
        // Format data for regulatory requirements (state-specific format)
        exportData = formatRegulatoryExport(data || [], filters.state);
        filename = `regulatory-certificate-export-${dateStr}.csv`;
        mimeType = 'text/csv';
      } else if (exportFormat === 'xlsx') {
        // Format data for Excel
        // In a real implementation, you'd use a library like SheetJS
        // For this example, we'll just use CSV
        exportData = formatCsvExport(data || []);
        filename = `certificates-export-${dateStr}.csv`;
        mimeType = 'text/csv';
      } else {
        // Default to CSV
        exportData = formatCsvExport(data || []);
        filename = `certificates-export-${dateStr}.csv`;
        mimeType = 'text/csv';
      }
      
      // Create a download link
      const blob = new Blob([exportData], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Create audit records
      const exportCount = data?.length || 0;
      const stateInfo = filters.state ? `for state ${filters.state}` : 'for all states';
      
      // Log the export in the audit system
      await createCertificateAuditRecord({
        certificateNumber: 'BATCH-EXPORT',
        actionType: 'viewed',
        status: 'exported',
        details: `Exported ${exportCount} certificates ${stateInfo} in ${exportFormat.toUpperCase()} format`
      });

      toast({
        title: 'Export Complete',
        description: `Successfully exported ${exportCount} certificates`,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Export Failed',
        description: `An error occurred: ${(error as Error).message}`,
      });
      console.error('Export error:', error);
    } finally {
      setIsExporting(false);
    }
  };
  
  // Format data for CSV export
  const formatCsvExport = (data: any[]): string => {
    // Define CSV headers
    const headers = [
      'Certificate Number',
      'State',
      'Course Type',
      'Status',
      'Assigned Date',
      'Reporting Status',
      'Verification Status',
      'Student Name',
      'Student Email',
      'Issue Date',
      'Revoked'
    ];
    
    // Format each row
    const rows = data.map(cert => [
      formatCertificateNumber(cert.certificate_number),
      cert.state,
      cert.course_type,
      cert.is_used ? 'Used' : 'Available',
      cert.assigned_at ? format(new Date(cert.assigned_at), 'MM/dd/yyyy') : '',
      cert.reporting_status || '',
      cert.verification_status || '',
      cert.certificates?.profiles?.full_name || '',
      cert.certificates?.profiles?.email || '',
      cert.certificates?.issued_at ? format(new Date(cert.certificates.issued_at), 'MM/dd/yyyy') : '',
      cert.certificates?.is_revoked ? 'Yes' : 'No'
    ]);
    
    // Combine headers and rows
    return [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
  };
  
  // Format data for regulatory export (state-specific format)
  const formatRegulatoryExport = (data: any[], state?: string): string => {
    // Different states might have different required formats
    // Here we'll implement a simple format for demonstration
    
    if (state === 'FL') {
      // Florida format (example)
      const headers = [
        'Certificate_Number',
        'Course_Type',
        'Student_Name',
        'Student_License',
        'Issue_Date',
        'Completion_Status'
      ];
      
      const rows = data.map(cert => [
        formatCertificateNumber(cert.certificate_number),
        cert.course_type,
        cert.certificates?.profiles?.full_name || '',
        cert.certificates?.profiles?.driver_license_number || '',
        cert.certificates?.issued_at ? format(new Date(cert.certificates.issued_at), 'MM/dd/yyyy') : '',
        cert.certificates ? 'COMPLETED' : 'PENDING'
      ]);
      
      return [
        headers.join('|'),
        ...rows.map(row => row.join('|'))
      ].join('\n');
    } else {
      // Generic format for other states
      return formatCsvExport(data);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Export Certificates
          </DialogTitle>
          <DialogDescription>
            Export certificate data for reporting or analysis. Customize the export with filters.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>State</Label>
              <Select
                value={filters.state}
                onValueChange={(value) => setFilters({...filters, state: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All States" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={undefined}>All States</SelectItem>
                  {metadata?.states.map(state => (
                    <SelectItem key={state} value={state}>{state}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Course Type</Label>
              <Select
                value={filters.courseType}
                onValueChange={(value) => setFilters({...filters, courseType: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Course Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={undefined}>All Course Types</SelectItem>
                  {metadata?.courseTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({...filters, startDate: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({...filters, endDate: e.target.value})}
              />
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="isUsed"
                checked={filters.isUsed === true}
                onCheckedChange={(checked) => {
                  if (checked === 'indeterminate') return;
                  setFilters({...filters, isUsed: checked ? true : undefined});
                }}
              />
              <Label htmlFor="isUsed">Used Certificates</Label>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="isVerified"
                checked={filters.isVerified === true}
                onCheckedChange={(checked) => {
                  if (checked === 'indeterminate') return;
                  setFilters({...filters, isVerified: checked ? true : undefined});
                }}
              />
              <Label htmlFor="isVerified">Verified Only</Label>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="isReported"
                checked={filters.isReported === true}
                onCheckedChange={(checked) => {
                  if (checked === 'indeterminate') return;
                  setFilters({...filters, isReported: checked ? true : undefined});
                }}
              />
              <Label htmlFor="isReported">Reported Only</Label>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Export Format</Label>
            <Select 
              value={exportFormat} 
              onValueChange={(value: 'csv' | 'xlsx' | 'regulatory') => setExportFormat(value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="csv">CSV (Standard)</SelectItem>
                <SelectItem value="xlsx">Excel (XLSX)</SelectItem>
                <SelectItem value="regulatory">
                  Regulatory Format
                  {filters.state && ` (${filters.state})`}
                </SelectItem>
              </SelectContent>
            </Select>
            
            <p className="text-xs text-muted-foreground">
              {exportFormat === 'regulatory' ? (
                <>
                  Regulatory format exports follow state-specific requirements for official reporting.
                  {!filters.state && (
                    <span className="text-amber-600"> Please select a state for proper formatting.</span>
                  )}
                </>
              ) : exportFormat === 'xlsx' ? (
                "Excel format includes additional metadata and is suitable for analysis."
              ) : (
                "CSV format is compatible with most spreadsheet applications."
              )}
            </p>
          </div>
          
          <div className="bg-muted p-3 rounded-md flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span>Preview:</span>
              {isCountLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <strong>{previewCount} certificates</strong>
              )} 
              will be exported
            </div>
            
            {previewCount && previewCount > 1000 && (
              <p className="text-xs text-amber-600">
                <AlertCircle className="h-3 w-3 inline mr-1" />
                Large export may take some time to process
              </p>
            )}
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isExporting}>
            Cancel
          </Button>
          <Button 
            onClick={handleExport} 
            disabled={isExporting || (previewCount || 0) === 0 || (exportFormat === 'regulatory' && !filters.state)}
            className="gap-2"
          >
            {isExporting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="h-4 w-4" />
                Export {previewCount} Certificates
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
